import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Pencil, Globe, GraduationCap, BookOpen, CheckCircle } from 'lucide-react';
import SEO from '../../../components/common/SEO';
import '../CoursePage.css';

const SIDEBAR_ITEMS = [
  { label: 'Duration', value: '3 Months', icon: <Calendar size={18} strokeWidth={1.8} /> },
  { label: 'Practice', value: 'Daily Writing Assignments', icon: <Pencil size={18} strokeWidth={1.8} /> },
  { label: 'Eligibility', value: 'Any UPSC / APSC Aspirant', icon: <GraduationCap size={18} strokeWidth={1.8} /> },
  { label: 'Mode', value: 'Offline + Online', icon: <Globe size={18} strokeWidth={1.8} /> },
  { label: 'Study Material', value: 'Model Answers + Feedback', icon: <BookOpen size={18} strokeWidth={1.8} /> },
  { label: 'Exams Covered', value: 'UPSC & APSC', icon: <CheckCircle size={18} strokeWidth={1.8} /> },
];

const TABS = ['Overview', 'Eligibility', 'Fees', 'Start Date', 'How to Apply'];

export default function AnswerWriting() {
  const [activeTab, setActiveTab] = useState('Overview');

  return (
    <div className="course-page">
      <SEO 
        title="Mains Answer Writing Practice Program"
        description="Master the art of answer writing for UPSC & APSC Mains. Daily answer checking, personalized feedback, and GS/optional subject-focused strategies."
      />
      <div className="course-breadcrumb">
        <div className="container course-breadcrumb__inner">
          <Link to="/" className="course-breadcrumb__link"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>Home</Link>
          <span className="course-breadcrumb__sep"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg></span>
          <Link to="/courses" className="course-breadcrumb__link">Courses</Link>
          <span className="course-breadcrumb__sep"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg></span>
          <span className="course-breadcrumb__current">Answer Writing Program</span>
        </div>
      </div>

      <div className="course-hero">
        <div className="container">
          <span className="course-hero__tag">Score Booster</span>
          <h1 className="course-hero__title">Answer Writing <span className="text-gold">Program</span></h1>
          <p className="course-hero__desc">Develop the structured, analytical writing skills that separate toppers from the rest — with daily practice and expert mentorship.</p>
          <div className="course-hero__meta">
            <span className="course-hero__meta-item"><Calendar size={15} strokeWidth={2} />3 Months</span>
            <span className="course-hero__meta-item"><Pencil size={15} strokeWidth={2} />Daily Practice</span>
            <span className="course-hero__meta-item"><Globe size={15} strokeWidth={2} />Offline + Online</span>
          </div>
        </div>
      </div>

      <section className="section course-overview">
        <div className="container course-overview__grid">
          <div className="course-overview__content">
            <div className="course-highlights">
              {[{ label: 'Duration', value: '3 Months' }, { label: 'Practice', value: 'Daily' }, { label: 'Mode', value: 'Offline / Online' }, { label: 'Focus', value: 'Mains Writing' }].map((h) => (
                <div key={h.label} className="course-highlight-item"><span className="course-highlight-value">{h.value}</span><span className="course-highlight-label">{h.label}</span></div>
              ))}
            </div>

            <div className="course-tabs">
              {TABS.map((tab) => (
                <button key={tab} className={`course-tab-btn${activeTab === tab ? ' course-tab-btn--active' : ''}`} onClick={() => setActiveTab(tab)}>{tab}</button>
              ))}
            </div>

            <div className="course-tab-content">
              {activeTab === 'Overview' && (
                <>
                  <h3>What's Included</h3>
                  <ul className="course-includes__list">
                    {['Daily answer writing assignments', 'Personalised feedback on every answer', 'Model answers from UPSC toppers', 'Introduction, body & conclusion structuring', 'Diagram & flowchart integration techniques', 'Essay writing workshops', 'Ethics & case study answer practice', 'Weekly performance review sessions'].map((p) => (
                      <li key={p}><span className="course-check"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg></span>{p}</li>
                    ))}
                  </ul>
                </>
              )}
              {activeTab === 'Eligibility' && (
                <>
                  <h3>Eligibility Criteria</h3>
                  <ul>
                    <li>Any UPSC or APSC aspirant preparing for Mains</li>
                    <li>Students who have cleared Prelims or are preparing simultaneously</li>
                    <li>No minimum qualification required</li>
                    <li>Basic writing ability in English or Hindi required</li>
                  </ul>
                </>
              )}
              {activeTab === 'Fees' && (
                <>
                  <h3>Fee Structure</h3>
                  <table className="course-fee-table">
                    <thead><tr><th>Component</th><th>Amount</th></tr></thead>
                    <tbody>
                      <tr><td>Program Fee (3 months)</td><td>₹15,000</td></tr>
                      <tr><td>Daily Assignments</td><td>Included</td></tr>
                      <tr><td>Personalised Feedback</td><td>Included</td></tr>
                      <tr><td>Model Answers</td><td>Included</td></tr>
                      <tr><td>Total</td><td><strong>₹15,000</strong></td></tr>
                    </tbody>
                  </table>
                  <p>One-time payment. No hidden charges.</p>
                </>
              )}
              {activeTab === 'Start Date' && (
                <>
                  <h3>Upcoming Batch Dates</h3>
                  <table className="course-fee-table">
                    <thead><tr><th>Batch</th><th>Start Date</th><th>Seats Available</th></tr></thead>
                    <tbody>
                      <tr><td>Batch A (Morning)</td><td>1st July 2025</td><td>20 Seats</td></tr>
                      <tr><td>Batch B (Online)</td><td>15th July 2025</td><td>30 Seats</td></tr>
                    </tbody>
                  </table>
                  <p>Daily sessions run Monday to Saturday. Flexible submission for online students.</p>
                </>
              )}
              {activeTab === 'How to Apply' && (
                <>
                  <h3>Application Process</h3>
                  <div className="course-steps">
                    {[
                      { title: 'Fill Enquiry Form', desc: 'Submit your details through our online enquiry form.' },
                      { title: 'Counselling Call', desc: 'Our team will guide you on the program structure.' },
                      { title: 'Fee Payment', desc: 'Pay the program fee to confirm your seat.' },
                      { title: 'Orientation', desc: 'Attend the orientation session and receive your writing schedule.' },
                      { title: 'Start Writing', desc: 'Begin daily assignments and receive feedback within 48 hours.' },
                    ].map((step, i) => (
                      <div key={step.title} className="course-step">
                        <div className="course-step__num">{i + 1}</div>
                        <div className="course-step__text"><strong>{step.title}</strong><span>{step.desc}</span></div>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact" state={{ course: 'Answer Writing Program' }} className="btn btn-primary">Apply Now →</Link>
                </>
              )}
            </div>

            {activeTab === 'Overview' && (
              <div className="course-cta">
                <Link to="/contact" state={{ course: 'Answer Writing Program' }} className="btn btn-primary">Enquire Now →</Link>
                <Link to="/contact" state={{ course: 'Answer Writing Program' }} className="btn btn-outline">Apply Now</Link>
              </div>
            )}
          </div>

          <aside className="course-sidebar">
            <div className="course-sidebar__card">
              <div className="course-sidebar__head"><h4 className="course-sidebar__title">Program Details</h4></div>
              <ul className="course-sidebar__list">
                {SIDEBAR_ITEMS.map((d) => (<li key={d.label}><span className="course-sidebar__icon">{d.icon}</span><div><span className="course-sidebar__label">{d.label}</span><span className="course-sidebar__value">{d.value}</span></div></li>))}
              </ul>
              <div className="course-sidebar__footer"><Link to="/contact" state={{ course: 'Answer Writing Program' }} className="btn btn-primary">Enroll Now →</Link></div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
